// ==========================================
// UPDATED: CreatePost.js
// BUG FIX: Guest/anonymous users can now create posts.
//   Previously the isAnonymous check showed a "sign in" prompt and blocked
//   the form entirely. The backend already allows guest posting via the
//   shared anonymous DB user, so the frontend just needs to show the form.
//   A subtle banner informs guests their post will be attributed to "Guest".
// ==========================================
import React, { useState } from 'react';
import { ImagePlus, MapPin, X, Info } from 'lucide-react';
import { usePosts } from '../../../hooks/usePosts';
import { useAuth } from '../../../hooks/useAuth';
import { MAX_FILE_SIZE } from '../../../utils/constants';
import LocationPicker from '../Search/LocationPicker';

// Dog breeds list
const DOG_BREEDS = [
  'Unknown', 'Labrador Retriever', 'Golden Retriever', 'German Shepherd',
  'French Bulldog', 'Bulldog', 'Poodle', 'Beagle', 'Dachshund',
  'Siberian Husky', 'Australian Shepherd', 'Rottweiler', 'Boxer',
  'Great Dane', 'Chihuahua', 'Shih Tzu', 'Mixed Breed', 'Other'
];

// Small informational banner for guest users — does NOT block posting
const GuestPostingNotice = () => (
  <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 mb-4">
    <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
    <p className="text-xs text-amber-800">
      You're posting as a <strong>Guest</strong>. Your post will be visible to the community.{' '}
      <a href="/login" className="underline font-semibold hover:text-amber-900">
        Sign in
      </a>{' '}
      to post under your own name and manage your posts later.
    </p>
  </div>
);

// CreatePost now renders the form for BOTH authenticated users AND guests
const CreatePost = ({ onPostCreated, currentCategory }) => {
  return <CreatePostForm onPostCreated={onPostCreated} currentCategory={currentCategory} />;
};

// ==========================================
// Actual form — rendered for all users including guests
// ==========================================
const CreatePostForm = ({ onPostCreated, currentCategory }) => {
  const { createPost } = usePosts();
  const { isAnonymous } = useAuth();

  const [content, setContent] = useState('');
  const [ownerMessage, setOwnerMessage] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [location, setLocation] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [error, setError] = useState('');

  const getInitialCategory = () => {
    if (currentCategory === 'missing') return 'missing';
    if (currentCategory === 'found') return 'found';
    if (currentCategory === 'success_stories') return 'success_stories';
    return 'general';
  };

  const fixedCategory = currentCategory === 'missing' ? 'missing' :
    currentCategory === 'found' ? 'found' :
    currentCategory === 'success_stories' ? 'success_stories' : null;

  const [category, setCategory] = useState(fixedCategory || getInitialCategory());
  const [petName, setPetName] = useState('');
  const [petType, setPetType] = useState('');
  const [breed, setBreed] = useState('Unknown');
  const [sex, setSex] = useState('unknown');
  const [missingSince, setMissingSince] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    try {
      const existingSize = mediaFiles.reduce((sum, file) => sum + file.size, 0);
      const newSize = files.reduce((sum, file) => sum + file.size, 0);
      if (existingSize + newSize > MAX_FILE_SIZE) {
        throw new Error(`Total size exceeds 25MB limit`);
      }
      setMediaFiles([...mediaFiles, ...files]);
    } catch (err) {
      alert(err.message);
    }
  };

  const removeMedia = (index) => setMediaFiles(mediaFiles.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if ((category === 'missing' || category === 'found') && !location) {
      setError('Location is required for Missing and Found posts');
      return;
    }
    if ((category === 'missing' || category === 'found') && !missingSince) {
      setError('Date is required for Missing and Found posts');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('content', content);
    formData.append('category', category);
    // NEW: append owner_message if provided
    if (ownerMessage.trim()) {
      formData.append('owner_message', ownerMessage.trim());
    }
    mediaFiles.forEach(file => formData.append('files', file));
    if (location) {
      formData.append('latitude', location.latitude.toString());
      formData.append('longitude', location.longitude.toString());
      formData.append('location_name', location.name);
    }
    if (petName) formData.append('pet_name', petName);
    if (petType) formData.append('pet_type', petType);
    if (breed && petType === 'dog') formData.append('breed', breed);
    if (sex) formData.append('sex', sex);
    if (missingSince) formData.append('missing_since', missingSince);
    if (contactInfo) formData.append('contact_info', contactInfo);
    try {
      await createPost(formData);
      setContent('');
      setOwnerMessage('');
      setMediaFiles([]);
      setLocation(null);
      setPetName(''); setPetType(''); setBreed('Unknown');
      setSex('unknown'); setMissingSince(''); setContactInfo('');
      setError('');
      onPostCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const needsPetInfo = ['missing', 'found', 'adoption'].includes(category);
  const locationRequired = ['missing', 'found'].includes(category);

  const getCreatePostTitle = () => {
    if (fixedCategory === 'found') return 'Report a Pet That You Found';
    if (fixedCategory === 'missing') return 'Create a Post Here to Report Your Missing Pet';
    if (fixedCategory === 'success_stories') return 'Share Your Success Story';
    return 'Create Post';
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">{getCreatePostTitle()}</h3>

        {/* Guest notice — informational only, does NOT block the form */}
        {isAnonymous && <GuestPostingNotice />}

        {/* Inline error */}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!fixedCategory && (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="general">General</option>
              <option value="adoption">Adoption</option>
              <option value="missing">Missing Pet</option>
              <option value="found">Found Pet</option>
              <option value="success_stories">Success Story</option>
            </select>
          )}

          {/* Description field */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={needsPetInfo ? "Describe the pet (appearance, size, colour, markings)..." : "What's on your mind?"}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            rows={3}
            required
          />

          {/* ── FIX 2: Owner Message — shown for missing/found/adoption posts ── */}
          {needsPetInfo && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Message from Owner <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={ownerMessage}
                onChange={(e) => setOwnerMessage(e.target.value)}
                placeholder={
                  category === 'missing'
                    ? "e.g. She responds to her name, loves treats, please call anytime day or night..."
                    : category === 'found'
                    ? "e.g. The dog seems friendly and well-fed, I have kept it safe at my home..."
                    : "e.g. This pet is house-trained, great with kids and other animals..."
                }
                className="w-full px-4 py-3 border border-amber-300 bg-amber-50 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none text-sm"
                rows={2}
              />
              <p className="text-xs text-gray-500 mt-1">
                An additional personal message displayed prominently on your post to help people understand the situation.
              </p>
            </div>
          )}

          {needsPetInfo && (
            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
              <input type="text" value={petName} onChange={(e) => setPetName(e.target.value)}
                placeholder="Pet Name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />

              <select value={petType} onChange={(e) => { setPetType(e.target.value); setBreed(e.target.value !== 'dog' ? '' : 'Unknown'); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none">
                <option value="">Select Pet Type</option>
                <option value="cat">Cat</option>
                <option value="dog">Dog</option>
                <option value="other">Other</option>
              </select>

              {petType === 'dog' && (
                <select value={breed} onChange={(e) => setBreed(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none">
                  {DOG_BREEDS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sex</label>
                <div className="flex gap-4">
                  {['male', 'female', 'unknown'].map(s => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="sex" value={s} checked={sex === s} onChange={(e) => setSex(e.target.value)} className="w-4 h-4" />
                      <span className="text-sm capitalize">{s}</span>
                    </label>
                  ))}
                </div>
              </div>

              {(category === 'missing' || category === 'found') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {category === 'missing' ? 'Missing Since' : 'Found On'} <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={missingSince} onChange={(e) => setMissingSince(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" required />
                </div>
              )}

              <input type="text" value={contactInfo} onChange={(e) => setContactInfo(e.target.value)}
                placeholder="Contact Info (Phone/Email)" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <input type="file" accept="image/*,video/*" onChange={handleMediaChange} className="hidden" id="media-upload" multiple />
            <label htmlFor="media-upload" className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition text-sm flex items-center gap-2">
              <ImagePlus size={18} />
              Add Media ({mediaFiles.length}) - Max 25MB
            </label>

            <button type="button" onClick={() => setShowLocationPicker(true)}
              className={`px-4 py-2 ${locationRequired ? 'bg-red-500' : 'bg-green-500'} text-white rounded-lg transition text-sm flex items-center gap-2`}>
              <MapPin size={18} />
              {location ? '✓ Location Set' : (locationRequired ? '* Add Location (Required)' : 'Add Location')}
            </button>

            <button type="submit" disabled={uploading}
              className="ml-auto px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-semibold disabled:opacity-50">
              {uploading ? 'Uploading...' : 'Post'}
            </button>
          </div>

          {mediaFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="w-full text-xs text-gray-700 mb-2">
                Total: {(mediaFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)).toFixed(1)}MB / 25MB
              </p>
              {mediaFiles.map((file, index) => (
                <div key={index} className="relative group">
                  <div className="w-20 h-20 bg-blue-200 rounded-lg overflow-hidden">
                    {file.type.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                    ) : <div className="w-full h-full flex items-center justify-center text-2xl">🎥</div>}
                  </div>
                  <button type="button" onClick={() => removeMedia(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <X size={14} />
                  </button>
                  <p className="text-xs text-center mt-1 text-gray-600">{(file.size / (1024 * 1024)).toFixed(1)}MB</p>
                </div>
              ))}
            </div>
          )}

          {location && (
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-green-50 p-3 rounded-lg border border-green-200">
              <MapPin size={16} className="text-green-600" />
              <div className="flex-1">
                <p className="font-semibold text-green-900">{location.name}</p>
                <p className="text-xs text-green-600">{location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</p>
              </div>
              <button type="button" onClick={() => setLocation(null)} className="text-red-500 hover:text-red-700">
                <X size={16} />
              </button>
            </div>
          )}
        </form>
      </div>

      {showLocationPicker && (
        <LocationPicker onLocationSelected={(loc) => setLocation(loc)} onClose={() => setShowLocationPicker(false)} />
      )}
    </>
  );
};

export default CreatePost;